import type MapboxDraw from '@mapbox/mapbox-gl-draw';
import type { Map as MapboxMap } from 'mapbox-gl';
import type { RefObject } from 'react';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { mapManager } from '@/services/map/MapManager';

interface MapboxContextValue {
  map: RefObject<MapboxMap | null>;
  draw: RefObject<MapboxDraw | null>;
  isMapReady: boolean;
  setMapReady: (ready: boolean) => void;
  mapManager: typeof mapManager;
}

interface MapboxProviderProps {
  children: ReactNode;
}

/**
 * Create the Mapbox Context
 */
const MapboxContext = createContext<MapboxContextValue | undefined>(undefined);

/**
 * MapboxProvider Component
 * Provides Mapbox map and draw instances to all child components
 * Also provides MapManager instance for map operations
 *
 * Note: The actual map initialization should be done in the component
 * that uses this provider (e.g., Map.js). This provider just makes
 * the refs available through context.
 */
export function MapboxProvider({ children }: MapboxProviderProps) {
  const map = useRef<MapboxMap | null>(null);
  const draw = useRef<MapboxDraw | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);

  // Initialize MapManager when map is ready
  useEffect(() => {
    if (isMapReady && map.current) {
      mapManager.initialize(map.current);
    }
  }, [isMapReady]);

  // Memoize the context value to prevent unnecessary re-renders
  // Refs are stable, but the object reference changes on each render
  const value: MapboxContextValue = useMemo(
    () => ({
      map,
      draw,
      isMapReady,
      setMapReady: setIsMapReady,
      mapManager,
    }),
    [isMapReady],
  );

  return <MapboxContext.Provider value={value}>{children}</MapboxContext.Provider>;
}

/**
 * useMapbox Hook
 * Hook to access the Mapbox context
 *
 * @throws {Error} If used outside of MapboxProvider
 * @returns The Mapbox context value
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { map, isMapReady } = useMapbox();
 *
 *   useEffect(() => {
 *     if (isMapReady && map.current) {
 *       map.current.flyTo({ center: [0, 0], zoom: 2 });
 *     }
 *   }, [isMapReady]);
 * }
 * ```
 */
export function useMapbox(): MapboxContextValue {
  const context = useContext(MapboxContext);

  if (context === undefined) {
    throw new Error(
      'useMapbox must be used within a MapboxProvider. ' +
        'Make sure to wrap your component tree with <MapboxProvider>.',
    );
  }

  return context;
}

/**
 * useMapManager Hook
 * Convenience hook to access MapManager instance
 *
 * @returns MapManager instance
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const mapManager = useMapManager();
 *
 *   useEffect(() => {
 *     mapManager.addLayer('my-layer', layerConfig, sourceConfig);
 *   }, []);
 * }
 * ```
 */
export function useMapManager() {
  const { mapManager } = useMapbox();
  return mapManager;
}

/**
 * useMapInstance Hook
 * Convenience hook to access the map instance directly
 *
 * @returns Map instance or null
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const map = useMapInstance();
 *
 *   useEffect(() => {
 *     if (map) {
 *       map.flyTo({ center: [0, 0], zoom: 2 });
 *     }
 *   }, [map]);
 * }
 * ```
 */
export function useMapInstance(): MapboxMap | null {
  const { map } = useMapbox();
  return map.current;
}
